// ScanTicket.tsx
import { useEffect, useState, useRef } from 'react';
import { Registration, UpdateRegistrationInput, Participation, CreateParticipationInput } from './API';
import { calculateCheckInStatus } from './utils/registrationStatus';
import { capitalizeFirstLetter } from './utils/strings';
import { updateRegistrationDetails } from './mutations/processUpdateRegistration';
import { createParticipationRecord } from './mutations/processCreateParticipation';
import CameraSelect from "./CameraSelect";
import VideoStream from './VideoStream';
import ScanResult from './ScanResult';
import { individualAttendanceEnabled } from './constants/attendance';
import { getParty } from './graphql/partyAttendance';
import { isIndividual, mergeParty, scannedRegistrationId } from './utils/partyAttendance';
import { PartyScanActions } from './components/PartyScanActions';

import { Button } from '@uclaea/react-component-library';
import './css/ScanResult.css';

interface ScanTicketProps {
  eventId: string;
  attendees: Registration[];
  handler: (result: Registration | null) => void;
  goBack: () => void;
  setAttendees: React.Dispatch<React.SetStateAction<Registration[]>>;
}

function ScanTicket(props: ScanTicketProps) {
  const {
    handler,
    goBack,
    attendees,
    setAttendees
  } = props;
  const scanLocked = useRef(false);
  const scanEpoch = useRef(0);
  useEffect(() => () => { scanEpoch.current++; }, []);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [hasScanned, setHasScanned] = useState<boolean>(false);
  const [validAttendee, setValidAttendee] = useState<Registration | null>(null);
  const [message, setMessage] = useState("Checking Access...");
  const [devicesAvailable, setDevicesAvailable] = useState<boolean>(false);
  const [scanningEnabled, setScanningEnabled] = useState<boolean>(true);
  const [scannerAttendees, setScannerAttendees] = useState<Registration[]>(attendees);

  useEffect(() => {
    setScannerAttendees(attendees);
    if (individualAttendanceEnabled) setValidAttendee(previous => {
      if (!previous) return null;
      const next = attendees.find(p => p.registrationId === previous.registrationId);
      return next ? mergeParty([previous], next)[0] : null;
    });
  }, [attendees]);

  async function handleScanResult(qrString: string) {
    if (individualAttendanceEnabled) {
      if (scanLocked.current) return;
      scanLocked.current = true;
      const epoch = ++scanEpoch.current;
      setHasScanned(true);
      setScanningEnabled(false);
      setValidAttendee(null);
      setMessage('Loading party…');
      try {
        const id = scannedRegistrationId(qrString, props.eventId);
        const party = await getParty(props.eventId, id);
        if (epoch !== scanEpoch.current) return;
        if (!party || !['REGISTERED', 'PARTIAL_CHECK_IN', ...(isIndividual(party) ? ['CHECKED_IN'] : [])].includes(party.registrationStatus)) {
          setMessage('This ticket is unavailable or cancelled.');
          return;
        }
        setValidAttendee(party);
        setAttendees(previous => mergeParty(previous, party));
        setMessage('');
      } catch { if (epoch !== scanEpoch.current) return; setMessage('Unable to load this ticket. Check the event, connection and sign-in, then scan again.'); }
      return;
    }
    setHasScanned(true);

    let qrEventId = "";
    let qrRegistrationId = "";

    try {
      const url = new URL(qrString);
      const qParam = url.searchParams.get('q');
      if (qParam) {
        [qrEventId, qrRegistrationId] = qParam.split(':');
      }
    } catch (e) {
      console.log("Scanned string is not a URL, treating as registrationId");
      qrRegistrationId = qrString;
    }

    const found = (props.eventId === qrEventId || !qrEventId) && scannerAttendees.find(attendee => qrRegistrationId === attendee.registrationId);
    console.log("found: ", found);

    if (found && found.registrationStatus !== "REGISTERED" && found.registrationStatus !== "PARTIAL_CHECK_IN") {
      let message = "";
      switch (found.registrationStatus) {
        case "CANCELLED":
          message = "Ticket has been Cancelled";
          break;
        case "CHECKED_IN":
          message = "Ticket has already been used to Check In";
          break;
        default:
          message = "Invalid ticket";
          break;
      }
      setMessage(message);
      setValidAttendee(null);
    } else if (found) {
      setValidAttendee(found || null);
      setMessage('');
    }

    setScanningEnabled(false);
  }

  async function handleAcknowledgeSingle() {
    await handleAcknowledge(false);
    resetScannerState(); // Reset the scanner state after check-in
  }
  
  async function handleAcknowledgeAll() {
    await handleAcknowledge(true);
    resetScannerState(); // Reset the scanner state after check-in
  }
  
  function resetScannerState() {
    scanLocked.current = false;
    scanEpoch.current++;
    setHasScanned(false);
    setScanningEnabled(true);
    setValidAttendee(null);  // Reset the validAttendee state
    setMessage("Checking Access...");  // Reset the message
  }

  async function handleAcknowledge(all: boolean) {
    if (!validAttendee || isIndividual(validAttendee)) return;
    const newCheckedInCount = all ? validAttendee.guests : (validAttendee.checkedInCount ?? 0) + 1;
    const guests = validAttendee.guests ?? 1;
    const registrationStatus = calculateCheckInStatus(newCheckedInCount, guests);

    const actorEmail = localStorage.getItem('kc_email');
    const registrationData: UpdateRegistrationInput = {
      eventId: validAttendee.eventId,
      registrationId: validAttendee.registrationId,
      checkedInCount: newCheckedInCount,
      registrationStatus: registrationStatus
    };
    const participationData: CreateParticipationInput = {
      eventId: validAttendee.eventId,
      registrationId: validAttendee.registrationId,
      actorEmail,
      actionType: "CHECK_IN",
      checkedInCount: newCheckedInCount,
      participationStatus: registrationStatus,
      userAgent: navigator.userAgent
    };

    try {
      const updatedAttendee = await updateRegistrationDetails(registrationData) as Registration;
      console.log('updatedAttendee: ', updatedAttendee);

      const createdParticipation = await createParticipationRecord(participationData) as unknown as Participation;
      console.log('createdParticipation: ', createdParticipation);

      // Update scannerAttendees state
      setScannerAttendees((prevAttendees) =>
        prevAttendees.map((attendee) =>
          attendee.registrationId === validAttendee.registrationId
            ? { ...attendee, ...updatedAttendee }
            : attendee
        )
      );

      // Update parent attendees state
      setAttendees((prevAttendees) =>
        prevAttendees.map((attendee) =>
          attendee.registrationId === validAttendee.registrationId
            ? { ...attendee, ...updatedAttendee }
            : attendee
        )
      );

      setValidAttendee(updatedAttendee);
      setHasScanned(false);
      setScanningEnabled(true);
      handler(updatedAttendee);
    } catch (err) {
      console.log("Encountered an error updating status.", err);
      alert("Error: Unable to update registration. Please check your network connection and try again.");
      setMessage("Encountered an error updating status.");
      setHasScanned(false);
      setScanningEnabled(true);
    }
  }

  function scanMore() {
    scanLocked.current = false;
    scanEpoch.current++;
    setHasScanned(false);
    setScanningEnabled(true);
    setValidAttendee(null);  // Reset the validAttendee state
    setMessage("Checking Access...");  // Reset the message  
  }

  useEffect(() => {
    navigator.mediaDevices.getUserMedia({
      video: true
    }).then(() => {
      setDevicesAvailable(true);
    }).catch((err: Error) => {
      console.log(err);
      if (typeof navigator.mediaDevices === 'undefined') {
        setMessage("Unable to access media, check SSL settings.");
      } else if (err.name === "NotAllowedError") {
        setMessage("Please grant this page permission to access your camera.");
      } else if (err.name === "OverconstrainedError") {
        setMessage("Resolution is not supported by your device.");
      } else {
        setMessage("An unknown error has occurred.");
      }
    });
  }, []);

  return devicesAvailable ? (
    <div className="scan-container">
      <div className="video-container">
        <VideoStream
          deviceId={deviceId}
          setScanResult={value => void handleScanResult(value)}
          disableScanning={!scanningEnabled}
          // highlightScanRegion={true}
          // highlightCodeOutline={true}
          maxScansPerSecond={50}
        />
        {hasScanned && (
          <div>
            <ScanResult isValid={validAttendee ? true : false}>
              {validAttendee && (
                <div className='scan-result-inner-container'>
                  Registrant:
                  <b>
                    <br />
                    {capitalizeFirstLetter(validAttendee.firstName)} {capitalizeFirstLetter(validAttendee.lastName)}
                  </b>
                  <br />
                  {validAttendee.checkedInCount} / {validAttendee.guests} Checked In
                  <br />
                  <br />
                  {validAttendee.live === false && "TEST REGISTRATION"}
                </div>
              )}
              {!validAttendee && (<div><b>Invalid Ticket</b><br />{message}</div>)}
              {validAttendee && isIndividual(validAttendee) && <PartyScanActions
                key={validAttendee.registrationId} party={validAttendee} onChange={party => {
                  setValidAttendee(previous => previous && mergeParty([previous], party)[0]);
                  setAttendees(previous => mergeParty(previous, party));
                  handler(party);
                }} />}
              <div className="scan-controls">
                {(!validAttendee || !isIndividual(validAttendee)) && <>
                <Button
                  variant="primary"
                  label="Check In"
                  disabled={!validAttendee}
                  onClick={() => {
                    handleAcknowledgeSingle().catch((err) => console.error("Failed to acknowledge", err));
                  }}
                />
                <Button
                  variant="primary"
                  label="Check In ALL"
                  disabled={!validAttendee}
                  onClick={() => {
                    handleAcknowledgeAll().catch((err) => console.error("Failed to acknowledge", err));
                  }}
                />
                </>}
                <Button
                  variant="secondary"
                  label="Cancel"
                  onClick={scanMore}
                />
              </div>
            </ScanResult>
          </div>
        )}
      </div>

      <div className="video-controls">
        <CameraSelect changeInputStream={setDeviceId} />
        <Button variant="secondary" label="Back to Event Details" onClick={goBack} />
      </div>
    </div>
  ) : (
    <p>{message}</p>
  );
}

export default ScanTicket;